import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Paper, IconButton, Tooltip, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Alert, Snackbar,
  LinearProgress, Stack, Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Science as DiscoverIcon,
  DataObject as SeedIcon,
  FiberManualRecord as DotIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { codeExtractionApi } from '../../services/api';


function ResultDisplay({ result, label }) {
  if (!result) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
      <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 300, overflow: 'auto' }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
          {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
        </pre>
      </Paper>
    </Box>
  );
}

function StalenessIndicator({ rule }) {
  const sampled = rule.retailers_sampled_count || 0;
  const current = rule.retailer_count || 0;

  if (!rule.extraction_pattern) {
    return (
      <Tooltip title="No extraction pattern" arrow>
        <DotIcon sx={{ fontSize: 12, color: 'grey.400' }} />
      </Tooltip>
    );
  }
  if (rule.is_stale) {
    return (
      <Tooltip title={`Stale: sampled ${sampled} retailers, now ${current} stock this brand`} arrow>
        <DotIcon sx={{ fontSize: 12, color: 'warning.main' }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`Up to date: ${sampled}/${current} retailers covered`} arrow>
      <DotIcon sx={{ fontSize: 12, color: 'success.main' }} />
    </Tooltip>
  );
}

// pattern_status comes from the API: a brand without a pattern is either unfinished
// (needs_pattern) or was rejected by the discovery promotion gate (the other two).
const STATUS_INFO = {
  needs_pattern: { label: 'Needs Pattern', color: 'warning' },
  low_coverage: { label: 'Low Coverage', color: 'error' },
  no_shared_codes: { label: 'No Shared Codes', color: 'default' },
  active: { label: 'Active', color: 'success' },
};

function getStatusInfo(rule) {
  return STATUS_INFO[rule.pattern_status] || STATUS_INFO.needs_pattern;
}

const STATUS_ORDER = Object.fromEntries(Object.keys(STATUS_INFO).map((key, i) => [key, i]));

function getSortValue(rule, field) {
  switch (field) {
    case 'brand': return rule.normalized_brand || '';
    case 'products': return rule.product_count || 0;
    case 'format': return rule.format_summary || '';
    case 'retailers': return rule.retailer_count || 0;
    case 'status': return STATUS_ORDER[rule.pattern_status] ?? 99;
    default: return 0;
  }
}

export default function BrandsTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Sorting
  const [sortField, setSortField] = useState('brand');
  const [sortDir, setSortDir] = useState('asc');

  // Filters
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Multi-select
  const [selectedBrands, setSelectedBrands] = useState(new Set());

  // Batch discover
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState('');

  const [reinferRunning, setReinferRunning] = useState(false);
  const [reinferProgress, setReinferProgress] = useState('');

  // Edit/Details Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [formCodeRegex, setFormCodeRegex] = useState('');
  const [formNote, setFormNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testRunning, setTestRunning] = useState(false);
  const [coverageWarning, setCoverageWarning] = useState(null);

  // Provide Examples Dialog
  const [examplesDialogOpen, setExamplesDialogOpen] = useState(false);
  const [seedBrand, setSeedBrand] = useState('');
  const [seedExamples, setSeedExamples] = useState('');
  const [seedCodeLevel, setSeedCodeLevel] = useState('unknown');
  const [seedRunning, setSeedRunning] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [seedExistingPattern, setSeedExistingPattern] = useState(null);

  // Single discover
  const [discoverRunning, setDiscoverRunning] = useState(null);


  const loadRules = useCallback(async (preserveMessages = false) => {
    setLoading(true);
    if (!preserveMessages) setError(null);
    try {
      const params = {};
      if (brandFilter) params.normalized_brand = brandFilter;
      const data = await codeExtractionApi.getBrandRules(params);
      let filtered = data.rules || [];
      if (statusFilter === 'stale') {
        filtered = filtered.filter(r => r.is_stale);
      } else if (statusFilter) {
        filtered = filtered.filter(r => r.pattern_status === statusFilter);
      }
      setRules(filtered);
      setSelectedBrands(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [brandFilter, statusFilter]);

  useEffect(() => { loadRules(); }, [loadRules]);

  // Sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'brand' ? 'asc' : 'desc');
    }
  };

  const sortedRules = [...rules].sort((a, b) => {
    const aVal = getSortValue(a, sortField);
    const bVal = getSortValue(b, sortField);
    const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Selection
  const handleToggleSelect = (brand) => {
    setSelectedBrands(prev => {
      const next = new Set(prev);
      if (next.has(brand)) next.delete(brand); else next.add(brand);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedBrands.size === sortedRules.length) {
      setSelectedBrands(new Set());
    } else {
      setSelectedBrands(new Set(sortedRules.map(r => r.normalized_brand)));
    }
  };

  // Batch Discover (sequential per-brand for live progress)
  const handleBatchDiscover = async () => {
    const brands = Array.from(selectedBrands);
    if (brands.length === 0) return;
    setBatchRunning(true);
    setError(null);
    let promoted = 0;
    let rejected = 0;
    let failed = 0;
    for (const brand of brands) {
      setBatchProgress(`Discovering ${promoted + rejected + failed + 1}/${brands.length}: ${brand}...`);
      try {
        const result = await codeExtractionApi.discoverBrandFormat({
          brand,
          products_per_retailer: 10,
        });
        const formats = result?.result?.code_formats || [];
        if (formats.length === 0) {
          failed++;
        } else if (result?.result?.extraction_pattern_persisted) {
          promoted++;
        } else {
          rejected++;
        }
      } catch (err) {
        failed++;
        console.error(`Discovery failed for ${brand}:`, err.message);
      }
    }
    setBatchProgress('');
    setBatchRunning(false);
    setSuccess(
      `Batch complete (${brands.length} brands): ${promoted} pattern saved, ` +
      `${rejected} format found but no new pattern saved (gate), ${failed} no format found`
    );
    setSelectedBrands(new Set());
    loadRules(true);
  };

  // Single Discover
  const handleDiscoverFormat = async (brand) => {
    setDiscoverRunning(brand);
    setError(null);
    try {
      const result = await codeExtractionApi.discoverBrandFormat({
        brand,
        products_per_retailer: 10,
      });
      const formats = result?.result?.code_formats || [];
      const sampled = result?.result?.products_sampled || 0;
      const errorMsg = result?.result?.error;
      const persisted = result?.result?.extraction_pattern_persisted;
      const formatsLabel = `${formats.length} format${formats.length !== 1 ? 's' : ''}`;
      if (formats.length === 0) {
        setError(errorMsg || `${brand}: no predictable code format found (${sampled} products sampled)`);
      } else if (persisted) {
        setSuccess(`${brand}: pattern saved — ${formatsLabel} found (${sampled} products sampled)`);
      } else {
        setError(`${brand}: ${formatsLabel} found, but the gate saved no new pattern — see the Status column`);
      }
      loadRules(true);
    } catch (err) { setError(err.message); }
    finally { setDiscoverRunning(null); }
  };

  // Self-healing re-inference (A1.3): re-run gated discovery for brittle brands.
  // The backend gate only promotes a re-inferred regex if it beats the incumbent
  // and clears the catalog-coverage floor, and never touches manually_set rules.
  const handleReinferBrittle = async () => {
    setReinferRunning(true);
    setReinferProgress('Starting…');
    setError(null);
    try {
      const start = await codeExtractionApi.reinferBrittle({});
      const jobId = start?.job_id;
      if (!jobId) {
        // Synchronous fallback (no brittle brands, or non-background response).
        const promoted = start?.promoted ?? 0;
        setSuccess(start?.status === 'completed' && !start?.count
          ? 'No brittle brands to re-infer'
          : `Re-inference complete — ${promoted} rule${promoted !== 1 ? 's' : ''} improved`);
        loadRules(true);
        return;
      }
      setReinferProgress(`Queued ${start.brands_queued || 0} brands…`);
      // Poll until the background job finishes. Discovery fills 0-80%; the
      // 80-100% band is the apply tail (re-extract + resync of promoted rules).
      while (true) {
        await new Promise((r) => setTimeout(r, 3000));
        const job = await codeExtractionApi.reinferStatus(jobId);
        if (job.progress_percent != null) {
          setReinferProgress(
            job.progress_percent < 80
              ? `${Math.round(job.progress_percent / 0.8)}% discovered`
              : 'applying promoted rules…'
          );
        }
        if (job.status === 'completed' || job.status === 'failed') {
          if (job.status === 'failed') {
            setError(`Re-inference job failed: ${job.error || 'unknown error'}`);
          } else {
            const promoted = job?.result?.promoted ?? 0;
            const completed = job?.result?.completed ?? 0;
            setSuccess(`Re-inference done — ${promoted}/${completed} rules improved`);
          }
          loadRules(true);
          break;
        }
      }
    } catch (err) { setError(err.message); }
    finally { setReinferRunning(false); setReinferProgress(''); }
  };

  // Edit/Details Dialog
  const handleOpenEdit = (rule) => {
    setEditRule(rule);
    setFormCodeRegex(rule.extraction_pattern?.code_regex || '');
    setFormNote(rule.extraction_pattern?.note || '');
    setTestResult(null);
    setCoverageWarning(null);
    setDialogOpen(true);
  };

  const handleSave = async (force = false) => {
    if (!editRule) return;
    setSaving(true);
    setError(null);
    setCoverageWarning(null);
    try {
      const data = {
        normalized_brand: editRule.normalized_brand,
      };
      if (formCodeRegex.trim()) {
        data.extraction_pattern = { code_regex: formCodeRegex.trim() };
        if (formNote.trim()) data.extraction_pattern.note = formNote.trim();
      } else {
        data.extraction_pattern = null;
      }
      if (force) data.force = true;
      if (editRule.id) {
        await codeExtractionApi.updateBrandRule(editRule.id, data);
      } else {
        await codeExtractionApi.createBrandRule(data);
      }
      const savedBrand = editRule.normalized_brand;
      const hadPattern = !!formCodeRegex.trim();
      setDialogOpen(false);
      // B1: apply the rule immediately (re-extract + re-sync) so the change takes
      // effect without a manual pipeline run. Only when a pattern was set.
      if (hadPattern && savedBrand) {
        setSuccess('Rule saved — applying…');
        try {
          await codeExtractionApi.reextractResync({ normalizedBrand: savedBrand });
          setSuccess(`Rule saved and applied to ${savedBrand}`);
        } catch (applyErr) {
          setError(`Rule saved, but applying it failed: ${applyErr.message}`);
        }
      } else {
        setSuccess('Rule saved');
      }
      loadRules();
    } catch (err) {
      // A3: pattern coverage too low → offer an explicit override rather than a generic error.
      if (err.status === 422 && err.body?.error === 'pattern_coverage_too_low') {
        setCoverageWarning(err.body);
      } else {
        setError(err.message);
      }
    } finally { setSaving(false); }
  };

  const handleTestPattern = async () => {
    if (!formCodeRegex.trim() || !editRule) return;
    setTestRunning(true);
    setTestResult(null);
    setCoverageWarning(null); // a fresh test supersedes any prior coverage rejection
    try {
      const result = await codeExtractionApi.testPattern({
        pattern_config: { code_regex: formCodeRegex.trim() },
        normalized_brand: editRule.normalized_brand,
      });
      setTestResult(result);
    } catch (err) { setError(err.message); }
    finally { setTestRunning(false); }
  };

  // Provide Examples
  const handleOpenExamples = (rule) => {
    setSeedBrand(rule.normalized_brand || '');
    setSeedExamples('');
    setSeedCodeLevel('unknown');
    setSeedResult(null);
    setSeedExistingPattern(rule.extraction_pattern);
    setExamplesDialogOpen(true);
  };

  const handleSeedFormat = async () => {
    if (!seedBrand.trim() || !seedExamples.trim()) {
      setError('Please enter brand name and example codes');
      return;
    }
    setSeedRunning(true);
    setSeedResult(null);
    setError(null);
    try {
      const codes = seedExamples.split('\n').map(c => c.trim()).filter(Boolean);
      const result = await codeExtractionApi.brandFormatFromExamples({
        brand: seedBrand.trim(),
        example_codes: codes,
        code_level: seedCodeLevel,
      });
      setSeedResult(result);
      setSuccess('Format inferred from examples');
      loadRules();
    } catch (err) { setError(err.message); }
    finally { setSeedRunning(false); }
  };


  return (
    <Box>
      {/* Filter bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small" label="Search Brand" value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            sx={{ width: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
              <MenuItem value="">All</MenuItem>
              {Object.entries(STATUS_INFO).map(([value, { label }]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
              <MenuItem value="stale">Stale</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {selectedBrands.size > 0 && (
            <Button
              variant="contained" color="secondary" size="small"
              startIcon={batchRunning ? <CircularProgress size={16} color="inherit" /> : <DiscoverIcon />}
              onClick={handleBatchDiscover}
              disabled={batchRunning}
            >
              {batchRunning ? batchProgress : `Discover Selected (${selectedBrands.size})`}
            </Button>
          )}
          <Button
            variant="outlined" size="small" color="secondary"
            startIcon={reinferRunning ? <CircularProgress size={16} color="inherit" /> : <DiscoverIcon />}
            onClick={handleReinferBrittle}
            disabled={reinferRunning || batchRunning}
          >
            {reinferRunning ? `Re-inferring ${reinferProgress}`.trim() : 'Re-infer Brittle'}
          </Button>
          <Tooltip
            arrow
            title={
              'Detects brands whose rule under-performs (≥20 failed extractions, ≤70% extract rate), ' +
              're-runs gated AI discovery for them, and only promotes a new regex if it passes the ' +
              'catalog-coverage gate — manually-set rules are never touched. Promoted rules are then ' +
              'automatically re-extracted and re-synced to Neo4j (the "applying" tail after discovery ' +
              'reaches 80%). Runs as a background job; safe to leave the page.'
            }
          >
            <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help', alignSelf: 'center' }} />
          </Tooltip>
          <Button startIcon={<RefreshIcon />} onClick={loadRules} size="small">Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)} message={success} />

      {batchRunning && <LinearProgress sx={{ mb: 2 }} />}

      {/* Brand Rules Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={sortedRules.length > 0 && selectedBrands.size === sortedRules.length}
                    indeterminate={selectedBrands.size > 0 && selectedBrands.size < sortedRules.length}
                    onChange={handleToggleSelectAll}
                  />
                </TableCell>
                <TableCell sortDirection={sortField === 'brand' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'brand'} direction={sortField === 'brand' ? sortDir : 'asc'} onClick={() => handleSort('brand')}>Brand</TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={sortField === 'products' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'products'} direction={sortField === 'products' ? sortDir : 'desc'} onClick={() => handleSort('products')}>Products</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'format' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'format'} direction={sortField === 'format' ? sortDir : 'asc'} onClick={() => handleSort('format')}>Format</TableSortLabel>
                </TableCell>
                <TableCell align="center" sortDirection={sortField === 'retailers' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'retailers'} direction={sortField === 'retailers' ? sortDir : 'desc'} onClick={() => handleSort('retailers')}>Retailers</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'status' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'status'} direction={sortField === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No brands found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedRules.map(rule => {
                  const status = getStatusInfo(rule);
                  return (
                    <TableRow key={rule.id || rule.normalized_brand} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedBrands.has(rule.normalized_brand)}
                          onChange={() => handleToggleSelect(rule.normalized_brand)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{rule.normalized_brand}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">{rule.product_count}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        {rule.format_summary ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {rule.format_summary}
                            </Typography>
                            {rule.format_examples?.length > 0 && (
                              <Typography
                                variant="body2" color="text.secondary"
                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mt: 0.25 }}
                              >
                                {rule.format_examples.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        ) : rule.extraction_pattern?.code_regex ? (
                          <Tooltip title={rule.extraction_pattern.note || ''} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace', fontSize: '0.8rem',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}
                            >
                              {rule.extraction_pattern.code_regex}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">&mdash;</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={`${rule.retailers_sampled_count || 0} sampled / ${rule.retailer_count || 0} current`} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              color: rule.is_stale ? 'warning.main' : 'text.secondary',
                              fontWeight: rule.is_stale ? 'bold' : 'normal',
                            }}
                          >
                            {rule.retailers_sampled_count || 0}/{rule.retailer_count || 0}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StalenessIndicator rule={rule} />
                          <Tooltip title={rule.pattern_status_reason || ''} arrow>
                            <Chip label={status.label} color={status.color} size="small" variant="outlined" />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Brand extraction details">
                          <IconButton size="small" onClick={() => handleOpenEdit(rule)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Seed format from example article numbers">
                          <IconButton size="small" color="primary" onClick={() => handleOpenExamples(rule)}>
                            <SeedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Discover Format (AI)">
                          <IconButton
                            size="small" color="secondary"
                            onClick={() => handleDiscoverFormat(rule.normalized_brand)}
                            disabled={discoverRunning !== null || batchRunning}
                          >
                            {discoverRunning === rule.normalized_brand ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DiscoverIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Brand Extraction Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Brand Extraction Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Read-only format info */}
            <TextField label="Brand Name" value={editRule?.normalized_brand || ''} disabled />

            {/* Discovered formats from extraction_pipeline */}
            {(editRule?.extraction_pipeline?.code_formats || []).length > 0 ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Discovered Formats ({editRule.extraction_pipeline.code_formats.length})
                </Typography>
                <Stack spacing={1.5}>
                  {editRule.extraction_pipeline.code_formats.map((fmt, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">{fmt.name}</Typography>
                        <Chip
                          label={`${Math.round((fmt.estimated_prevalence || 0) * 100)}%`}
                          size="small" variant="outlined"
                          color={fmt.estimated_prevalence >= 0.3 ? 'primary' : 'default'}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                        {fmt.description}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}
                      >
                        {fmt.regex}
                      </Typography>
                      {fmt.examples?.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mt: 0.5 }}>
                          {fmt.examples.slice(0, 5).join(', ')}
                        </Typography>
                      )}
                      {fmt.has_color_suffix && (
                        <Chip label={`color suffix: "${fmt.color_suffix_separator || ''}"`} size="small" sx={{ mt: 0.5, fontSize: '0.7rem' }} />
                      )}
                    </Paper>
                  ))}
                </Stack>
                <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Retailers sampled: {editRule.retailers_sampled_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Retailers current: {editRule.retailer_count || 0}
                  </Typography>
                  {editRule.is_stale && (
                    <Chip label="Stale" color="warning" size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            ) : editRule?.format_summary ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Discovered Format</Typography>
                <Typography variant="body2">{editRule.format_summary}</Typography>
              </Paper>
            ) : null}

            {/* Editable fields */}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Extraction Pattern</Typography>
            <TextField
              label="Code Regex"
              value={formCodeRegex}
              onChange={(e) => setFormCodeRegex(e.target.value)}
              placeholder='e.g. \d{4,6} or (?:\d{7})|(?:\d{5})'
              helperText="Regex applied via re.search() on the supplier code field. Full match = the extracted code."
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }}
            />
            <TextField
              label="Note (optional)"
              value={formNote}
              onChange={(e) => setFormNote(e.target.value)}
              placeholder="e.g. Both numeric and name-number formats"
              size="small"
            />

            {/* Test Pattern */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined" size="small"
                onClick={handleTestPattern}
                disabled={!formCodeRegex.trim() || testRunning}
              >
                {testRunning ? 'Testing...' : 'Test Pattern'}
              </Button>
              {testRunning && <CircularProgress size={18} />}
              {testResult?.coverage && (
                <Typography variant="body2" sx={{ color: testResult.passes ? 'success.main' : 'error.main' }}>
                  {Math.round((testResult.coverage.match_rate || 0) * 100)}% coverage
                  {' '}({testResult.coverage.matched}/{testResult.coverage.tested})
                  {testResult.passes ? ' ✓' : ` — below ${Math.round((testResult.min_match_rate || 0.7) * 100)}% min`}
                </Typography>
              )}
            </Box>
            {coverageWarning && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {coverageWarning.message}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          {coverageWarning ? (
            <Button onClick={() => handleSave(true)} variant="contained" color="warning" disabled={saving}>
              {saving ? 'Saving...' : 'Save anyway'}
            </Button>
          ) : (
            <Button onClick={() => handleSave(false)} variant="contained" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Provide Examples Dialog */}
      <Dialog open={examplesDialogOpen} onClose={() => setExamplesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Seed Brand Format from Examples</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste example supplier article numbers (e.g., from the brand's website).
            This will infer the code format and update the extraction pattern.
          </Typography>
          {seedExistingPattern && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Current regex:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {seedExistingPattern.code_regex}
              </Typography>
            </Alert>
          )}
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField
                size="small" label="Brand Name"
                value={seedBrand}
                onChange={(e) => setSeedBrand(e.target.value)}
                placeholder="summum"
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Code Level</InputLabel>
                <Select value={seedCodeLevel} onChange={(e) => setSeedCodeLevel(e.target.value)} label="Code Level">
                  <MenuItem value="unknown">Unknown</MenuItem>
                  <MenuItem value="style">Style (base code)</MenuItem>
                  <MenuItem value="color">Color (variant code)</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TextField
              multiline rows={5} label="Example Article Numbers (one per line)"
              value={seedExamples}
              onChange={(e) => setSeedExamples(e.target.value)}
              placeholder={"2S3375-12326\n4S2834-10944\n1S1257-12277"}
              fullWidth
            />
          </Stack>
          {seedRunning && <LinearProgress sx={{ mt: 2 }} />}
          <ResultDisplay result={seedResult} label="Inferred Format" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExamplesDialogOpen(false)}>Close</Button>
          <Button onClick={handleSeedFormat} variant="contained" disabled={seedRunning}>
            {seedRunning ? 'Inferring...' : 'Infer Format'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
